import { useId } from "react";
import type { GridHour } from "../../../shared/weatherDetail";
import { useHousehold } from "../../app/useHousehold";
import { dataStateOf } from "../../data/queries";
import { useGrid } from "../../data/weatherQueries";
import { EmptyState } from "../../ui/StatusBadge";
import { relativeAge, useNow } from "../../ui/time";
import { NeedsLocation, remPx, SkyFoot, useBoxSize, useTimeFmt } from "../sky/common";
import { smoothPath } from "../sky/HourlyGraphWidget";
import type { WidgetDefinition, WidgetProps } from "../types";
import { WidgetHeader } from "../WidgetHeader";
import { units } from "./units";
import "./nerd.css";

interface GraphsConfig {
  hours: number;
}

/**
 * A meteogram: temperature with dew point and feels-like, precipitation
 * amounts and chance, wind and gusts, and cloud cover, on one shared time
 * axis. From the NWS gridpoint forecast, the data behind every NWS forecast.
 */
function ForecastGraphs({ config, size, fullscreen, openFullscreen }: WidgetProps<GraphsConfig>) {
  const { settings } = useHousehold();
  useNow("minute");
  const q = useGrid();
  const state = dataStateOf(q, 90 * 60_000);
  if (!settings.location) return <NeedsLocation icon="thermo" title="Forecast graphs need a home location" detail="Set a general location to see the detailed forecast." compact={size.variant === "compact"} />;
  const data = q.data;
  if (!data) return state === "loading" ? <div className="widget-loading" /> : <EmptyState icon="cloud" title="Detailed forecast unavailable" detail={q.error?.message} />;
  const now = Date.now();
  const hours = data.hours.filter((h) => Date.parse(h.t) + 3_600_000 > now).slice(0, fullscreen ? 48 : config.hours);
  if (hours.length < 6) return <EmptyState icon="cloud" title="No detailed forecast right now" />;

  const u = units(settings.temperatureUnit);
  const day = hours.slice(0, 24);
  const sum = (pick: (h: GridHour) => number | null) => day.reduce((s, h) => s + (pick(h) ?? 0), 0);
  const rain = sum((h) => h.qpfMm);
  const snow = sum((h) => h.snowMm);
  const gust = Math.max(0, ...day.map((h) => h.gustKph ?? 0));
  const thunder = Math.max(0, ...day.map((h) => h.thunderPct ?? 0));
  const facts = [
    rain >= 0.25 ? `${u.rainLabel(rain)} rain` : "Dry",
    snow >= 2.5 ? `${u.snowLabel(snow)} snow` : null,
    gust > 0 ? `gusts ${u.speedLabel(gust)}` : null,
    thunder >= 15 ? `thunder ${thunder}%` : null,
  ].filter(Boolean);

  return (
    <div className="nerd nerd-graphs">
      <WidgetHeader title={`Next ${hours.length} hours, in detail`} meta={`Next 24 h: ${facts.join(" · ")}`} onTitle={fullscreen ? undefined : openFullscreen} />
      <Meteogram hours={hours} compact={size.variant === "compact" || size.height < remPx() * 16.9} />
      {size.variant !== "compact" && <SkyFoot source={`NWS gridpoint forecast${data.updatedAt ? ` · updated ${relativeAge(data.updatedAt)}` : ""}`} stamp={data.stamp} state={state} updatedAt={q.dataUpdatedAt} />}
    </div>
  );
}

function Meteogram({ hours, compact }: { hours: GridHour[]; compact: boolean }) {
  const { settings, timeZone } = useHousehold();
  const u = units(settings.temperatureUnit);
  const fmt = useTimeFmt();
  const [ref, box] = useBoxSize<HTMLDivElement>();
  const uid = useId().replace(/[^a-zA-Z0-9-]/g, "");
  const W = Math.max(240, box.width);
  const H = Math.max(160, box.height);
  const padL = 34;
  const padR = 12;
  const axisH = 18;
  const hasSnow = hours.some((h) => (h.snowMm ?? 0) > 0.2);
  // Panels top to bottom; wind and clouds drop out when space is short.
  const panels = compact ? (["temp", "precip"] as const) : (["temp", "precip", "wind", "sky"] as const);
  const weights = { temp: 3, precip: 1.6, wind: 1.4, sky: 0.7 };
  const total = panels.reduce((s, p) => s + weights[p], 0);
  // Room between panels for the axis labels on either side of a boundary.
  const gap = 16;
  // The top 16 px is the weekday row.
  const usable = H - axisH - gap * (panels.length - 1) - 16;
  const bands: Record<string, { top: number; bottom: number }> = {};
  let yCursor = 16;
  for (const p of panels) {
    const hgt = (usable * weights[p]) / total;
    bands[p] = { top: yCursor, bottom: yCursor + hgt };
    yCursor += hgt + gap;
  }
  const n = hours.length;
  const step = (W - padL - padR) / (n - 1);
  const x = (i: number) => padL + i * step;
  const every = Math.max(1, Math.ceil(46 / step));

  // Local-midnight dividers with the weekday.
  const dayLines = hours
    .map((h, i) => ({ i, date: new Date(h.t) }))
    .filter(({ date }) => new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", hourCycle: "h23" }).format(date) === "0");

  // Temperature, dew point, feels-like.
  const t = bands.temp!;
  const temps = hours.map((h) => (h.tempC === null ? null : u.temp(h.tempC)));
  const dews = hours.map((h) => (h.dewC === null ? null : u.temp(h.dewC)));
  const feels = hours.map((h) => (h.feelsC === null ? null : u.temp(h.feelsC)));
  const all = [...temps, ...dews, ...feels].filter((v): v is number => v !== null);
  const lo = Math.floor(Math.min(...all) / 5) * 5;
  const hi = Math.ceil(Math.max(...all) / 5) * 5 + 2;
  const ty = (v: number) => t.bottom - ((v - lo) / Math.max(5, hi - lo)) * (t.bottom - t.top);
  const path = (vals: (number | null)[]) => smoothPath(vals.flatMap((v, i) => (v === null ? [] : [{ x: x(i), y: ty(v) }])));
  const tVals = temps.filter((v): v is number => v !== null);
  const tMax = Math.max(...tVals);
  const tMin = Math.min(...tVals);
  const iMax = temps.indexOf(tMax);
  const iMin = temps.indexOf(tMin);
  const showFeels = feels.some((f, i) => f !== null && temps[i] !== null && Math.abs(f - temps[i]!) >= 3);

  // Precipitation: bars for amount, line for chance.
  const p = bands.precip!;
  const amounts = hours.map((h) => u.rain(h.qpfMm ?? 0));
  const snows = hours.map((h) => u.rain(h.snowMm ?? 0) / 10); // snow depth → rough liquid scale for the bar height
  const maxAmt = Math.max(u.rain(2.5), ...amounts, ...snows);
  const py = (v: number) => p.bottom - (v / maxAmt) * (p.bottom - p.top);
  const popY = (v: number) => p.bottom - (v / 100) * (p.bottom - p.top);
  const barW = Math.max(2, step * 0.7);

  return (
    <>
    <ul className="mg-legend" aria-hidden="true">
      <li className="mg-l-temp">Temp</li>
      <li className="mg-l-dew">Dew point</li>
      {showFeels && <li className="mg-l-feels">Feels like</li>}
      <li className="mg-l-rain">Rain{hasSnow ? " · snow" : ""}</li>
      <li className="mg-l-pop">Chance</li>
      {bands.wind && <li className="mg-l-wind">Wind ({u.speedUnit})</li>}
      {bands.wind && <li className="mg-l-gust">Gusts</li>}
      {bands.sky && <li className="mg-l-sky">Clouds</li>}
    </ul>
    <div className="meteogram" ref={ref}>
      {box.width > 0 && (
        <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label="Detailed forecast graphs">
          <defs>
            <linearGradient id={`${uid}-t`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="var(--accent)" stopOpacity="0.28" />
              <stop offset="1" stopColor="var(--accent)" stopOpacity="0" />
            </linearGradient>
          </defs>
          {dayLines.map(({ i, date }) => (
            <g key={`d${i}`}>
              <line x1={x(i)} x2={x(i)} y1={2} y2={H - axisH} className="mg-day" />
              <text x={x(i) + 4} y={12} className="mg-dayname">
                {new Intl.DateTimeFormat("en-US", { timeZone, weekday: "short" }).format(date)}
              </text>
            </g>
          ))}

          {/* Temperature panel */}
          <text x={2} y={t.top + 10} className="mg-axis">
            {Math.round(hi)}°
          </text>
          <text x={2} y={t.bottom - 3} className="mg-axis">
            {Math.round(lo)}°
          </text>
          <path d={`${path(temps)} L${x(n - 1)} ${t.bottom} L${x(0)} ${t.bottom} Z`} fill={`url(#${uid}-t)`} />
          {showFeels && <path d={path(feels)} className="mg-feels" />}
          <path d={path(dews)} className="mg-dew" />
          <path d={path(temps)} className="mg-temp" />
          {[iMax, iMin].map((i) => {
            const cy = ty(temps[i]!);
            // Above the high, below the low, but never outside the panel.
            const below = i === iMin && cy + 16 <= t.bottom;
            return (
              <g key={`x${i}`}>
                <circle cx={x(i)} cy={cy} r={3.5} className="mg-temp-dot" />
                <text x={Math.min(Math.max(x(i), padL + 12), W - padR - 12)} y={below ? cy + 16 : Math.max(t.top + 10, cy - 8)} className="mg-temp-label">
                  {Math.round(temps[i]!)}°
                </text>
              </g>
            );
          })}

          {/* Precipitation panel */}
          <line x1={padL} x2={W - padR} y1={p.bottom} y2={p.bottom} className="mg-base" />
          {hours.map((_h, i) =>
            amounts[i]! > 0 ? <rect key={`r${i}`} x={x(i) - barW / 2} y={py(amounts[i]!)} width={barW} height={p.bottom - py(amounts[i]!)} rx={1.5} className="mg-rain" /> : null,
          )}
          {hasSnow &&
            hours.map((_h, i) => (snows[i]! > 0 ? <rect key={`s${i}`} x={x(i) - barW / 2} y={py(snows[i]!)} width={barW} height={p.bottom - py(snows[i]!)} rx={1.5} className="mg-snow" /> : null))}
          <path d={smoothPath(hours.map((h, i) => ({ x: x(i), y: popY(h.pop ?? 0) })))} className="mg-pop" />
          {hours.map((h, i) =>
            (h.thunderPct ?? 0) >= 15 ? (
              <text key={`th${i}`} x={x(i)} y={p.top + 10} className="mg-thunder">
                ⚡
              </text>
            ) : null,
          )}
          <text x={2} y={p.top + 10} className="mg-axis">
            {settings.temperatureUnit === "F" ? `${maxAmt.toFixed(2)}″` : `${maxAmt.toFixed(1)}mm`}
          </text>

          {/* Wind panel */}
          {bands.wind && <Wind band={bands.wind} hours={hours} x={x} every={Math.max(every, 3)} W={W} padL={padL} padR={padR} />}

          {/* Cloud cover */}
          {bands.sky && (
            <path
              d={`M${x(0)} ${bands.sky.bottom} ${hours.map((h, i) => `L${x(i)} ${bands.sky!.bottom - ((h.sky ?? 0) / 100) * (bands.sky!.bottom - bands.sky!.top)}`).join(" ")} L${x(n - 1)} ${bands.sky.bottom} Z`}
              className="mg-sky"
            />
          )}

          {hours.map((h, i) =>
            i % every === 0 ? (
              <text key={`h${i}`} x={x(i)} y={H - 4} className="mg-hour">
                {i === 0 ? "Now" : fmt.hour(new Date(h.t))}
              </text>
            ) : null,
          )}
        </svg>
      )}
    </div>
    </>
  );
}

function Wind({ band, hours, x, every, W, padL, padR }: { band: { top: number; bottom: number }; hours: GridHour[]; x: (i: number) => number; every: number; W: number; padL: number; padR: number }) {
  const { settings } = useHousehold();
  const u = units(settings.temperatureUnit);
  const speeds = hours.map((h) => u.speed(h.windKph ?? 0));
  const gusts = hours.map((h) => u.speed(h.gustKph ?? h.windKph ?? 0));
  const max = Math.max(10, ...gusts);
  const y = (v: number) => band.bottom - (v / max) * (band.bottom - band.top - 12);
  return (
    <>
      <line x1={padL} x2={W - padR} y1={band.bottom} y2={band.bottom} className="mg-base" />
      <path d={`${smoothPath(gusts.map((g, i) => ({ x: x(i), y: y(g) })))}`} className="mg-gust" />
      <path d={smoothPath(speeds.map((s, i) => ({ x: x(i), y: y(s) })))} className="mg-wind" />
      {hours.map((h, i) =>
        i % every === 0 && h.windDeg !== null ? (
          // Arrows point where the wind blows to (from the direction it comes from).
          <g key={`w${i}`} transform={`translate(${x(i)} ${band.top + 6}) rotate(${h.windDeg + 180})`}>
            <path d="M0 -5 L3 3 L0 1.5 L-3 3 Z" className="mg-arrow" />
          </g>
        ) : null,
      )}
      <text x={2} y={band.top + 12} className="mg-axis">
        {Math.round(max)}
      </text>
    </>
  );
}

export const forecastGraphsWidget: WidgetDefinition<GraphsConfig> = {
  type: "forecast-graphs",
  version: 1,
  name: "Forecast graphs",
  description: "The weather nerd’s view: temperature with dew point and feels-like, rain and snow amounts, wind and gusts, and cloud cover, hour by hour.",
  icon: "thermo",
  category: "weather",
  defaultSize: { w: 8, h: 4 },
  minSize: { w: 4, h: 3 },
  stackHeight: 420,
  defaults: { hours: 36 },
  settings: [
    {
      key: "hours",
      label: "Hours shown",
      type: "select",
      options: [
        { value: 24, label: "24 hours" },
        { value: 36, label: "36 hours" },
        { value: 48, label: "48 hours" },
      ],
    },
  ],
  provider: "nws",
  capabilities: { requiresLocation: true },
  supportsFullscreen: true,
  Component: ForecastGraphs,
};
